import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { UserDto, Role } from '../types/api';

export const userKeys = {
  all:    ()           => ['users']       as const,
  detail: (id: string) => ['users', id]  as const,
};

export interface CreateUserRequest {
  name: string;
  email: string;
  role: Role;
  baselinePoints: number;
  baselineCycleDays: number;
  team?: string;
}

export interface UpdateUserRequest {
  role?: Role;
  isActive?: boolean;
  unlockAccount?: boolean;
  team?: string;
}

export function useUserList() {
  return useQuery({
    queryKey: userKeys.all(),
    queryFn:  () => client.get<UserDto[]>('/users').then((r) => r.data),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) =>
      client.post<UserDto>('/users', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all() }),
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateUserRequest) =>
      client.patch<UserDto>(`/users/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all() }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all() }),
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: (id: string) => client.post(`/users/${id}/resend-invite`),
  });
}
